import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Cpu, DatabaseBackup, Download, ImageUp, Plus, Sparkles, Trash2, X } from 'lucide-react';
import {
  OLT_IMAGE_CONTENT_TYPES,
  OLT_IMAGE_MAX_BYTES,
  PARAM_FIELDS,
  PARAM_FIELD_LABELS,
  type DataModel,
  type Olt,
  type OltBackup,
  type OnuType,
  type Page,
  type ParamField,
  type SeedOnuTypesResult,
  type SeededOnuModel,
  type ModelResearch,
  type UnmappedModel,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatTimestamp } from '../lib/format';
import { useSession } from '../lib/session';
import { useTasks } from '../lib/tasks';
import { WizardDialog } from '../components/WizardDialog';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

/** One model in the hardware library, as `/api/onu-hardware` returns it. */
interface OnuHardware {
  id: string;
  name: string;
  vendor: string | null;
  ponType: string;
  ethPorts: number | null;
  /** SSID interfaces the chassis lists for this model — 8, 4 and 1 all occur. */
  wifiSsids: number | null;
  /**
   * The radios this model is known to have. Null is unrecorded, and unrecorded
   * means the fleet-wide SSID layout applies. A recorded set without 5G is what
   * makes a 5 GHz preset refuse by name on this model instead of writing to an
   * interface it does not have.
   */
  ssidBands: string[] | null;
  ssidBandsSource: string | null;
  voipPorts: number | null;
  catv: number | null;
  source: string;
  derivedFrom: string | null;
  capability: string | null;
  notes: string | null;
  /**
   * The uploaded picture of the model, or null. Same shape as the OLT
   * photograph; the bytes are at `/api/onu-hardware/:id/image?v=<uploadedAt>`,
   * and SmartOLT's `get_onu_type_image` serves the same object.
   */
  image: { contentType: string; size: number; uploadedAt: number } | null;
  onuCount: number;
}

/** Same picture, same URL, one fetch — a new URL exactly when it changed. */
function hardwareImageUrl(h: Pick<OnuHardware, 'id' | 'image'>): string | null {
  if (!h.image) return null;
  return `/api/onu-hardware/${encodeURIComponent(h.id)}/image?v=${h.image.uploadedAt}`;
}

/**
 * The picture goes up as raw bytes, like the OLT photograph and firmware:
 * what KIND of image it is, the API decides from the file's own contents.
 */
async function uploadHardwareImage(id: string, file: File): Promise<void> {
  const res = await fetch(`/api/onu-hardware/${encodeURIComponent(id)}/image`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Upload failed (${res.status})`);
  }
}

async function removeHardwareImage(id: string): Promise<void> {
  const res = await fetch(`/api/onu-hardware/${encodeURIComponent(id)}/image`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Could not remove the picture (${res.status})`);
}

interface HardwareResponse {
  items: OnuHardware[];
  missing: Array<{ name: string; onuCount: number; vendor: string | null }>;
}

interface ImportResult {
  created: number;
  updated: number;
  skippedOperator: number;
  fromInventory: string[];
  unnamed: number;
}

/**
 * The library of ONU models, in two halves that answer different questions.
 *
 * The hardware half is what an operator means by "ONU types": which models the
 * network runs and what each has on the back of it. It is read from the OLTs,
 * which already declare it, so it fills itself for a fleet that has never
 * spoken TR-069 — which is most fleets, most of the time.
 *
 * The parameter half maps where each setting lives per CPE model, and only a
 * model that has informed an ACS can have one. It is kept below rather than
 * removed: it is the half that matters once TR-069 is running, and an empty
 * section that explains itself beats a missing one.
 */
export function OnuTypesPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const editable = can('settings.manage');

  const [editing, setEditing] = useState<OnuType | null>(null);
  const [cloning, setCloning] = useState<OnuType | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingModel, setEditingModel] = useState<OnuHardware | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const hardwareQuery = useQuery({
    queryKey: ['onu-hardware'],
    queryFn: () => api.get<HardwareResponse>('/onu-hardware'),
  });
  const hardware = hardwareQuery.data?.items ?? [];
  const missing = hardwareQuery.data?.missing ?? [];

  const importHardware = useMutation({
    mutationFn: () => api.post<ImportResult>('/onu-hardware/import', { dryRun: false }),
    onSuccess: async (r) => {
      const parts = [`${r.created} added`, `${r.updated} updated`];
      if (r.skippedOperator > 0) parts.push(`${r.skippedOperator} left alone (you edited them)`);
      setNotice(
        `Read the OLT configs: ${parts.join(', ')}.` +
          (r.unnamed > 0
            ? ` ${r.unnamed} ONUs report no model at all, so those cannot be counted here.`
            : ''),
      );
      await queryClient.invalidateQueries({ queryKey: ['onu-hardware'] });
    },
    onError: (err) => setNotice(err instanceof Error ? err.message : 'Import failed'),
  });

  const typesQuery = useQuery({
    queryKey: ['onu-types'],
    queryFn: () => api.get<Page<OnuType>>('/onu-types'),
  });

  const researchQuery = useQuery({
    queryKey: ['onu-types', 'research'],
    queryFn: () => api.get<Page<ModelResearch>>('/onu-types/research'),
  });
  const [researchNote, setResearchNote] = useState<string | null>(null);
  const runResearch = useMutation({
    mutationFn: () => api.post<{ researched: string[]; skipped: Array<{ model: string; why: string }> }>('/onu-types/research/run', {}),
    onSuccess: async (r) => {
      setResearchNote(
        r.researched.length === 0 && r.skipped.length === 0
          ? 'Nothing owed: every model has been researched since its newest unit was seen.'
          : `Researched ${r.researched.length === 0 ? 'none' : r.researched.join(', ')}${r.skipped.length > 0 ? `; skipped ${r.skipped.map((s) => `${s.model} (${s.why})`).join('; ')}` : ''}.`,
      );
      await queryClient.invalidateQueries({ queryKey: ['onu-types', 'research'] });
    },
    onError: (err) => setResearchNote(err instanceof Error ? err.message : 'The research did not run'),
  });
  const unmappedQuery = useQuery({
    queryKey: ['onu-types', 'unmapped'],
    queryFn: () => api.get<Page<UnmappedModel>>('/onu-types/unmapped'),
  });

  const discover = useMutation({
    mutationFn: (deviceId: string) => api.post<OnuType>('/onu-types/discover', { deviceId }),
    onSuccess: async (type) => {
      setNotice(`Discovered a mapping for ${type.manufacturer} ${type.productClass}`);
      await queryClient.invalidateQueries({ queryKey: ['onu-types'] });
    },
    onError: (err) => setNotice(err instanceof Error ? err.message : 'Discovery failed'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/onu-types/${encodeURIComponent(id)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onu-types'] }),
  });

  const types = typesQuery.data?.items ?? [];
  const unmapped = unmappedQuery.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="ONU types"
        description="The models your network runs, read from the OLTs, and where each setting lives on the ones that speak TR-069."
        actions={
          editable ? (
            <div className="flex flex-wrap gap-2">
              <Button
                icon={<Download className="size-4" />}
                loading={importHardware.isPending}
                onClick={() => importHardware.mutate()}
                title="Read the latest OLT configs and fill in the models they declare"
              >
                Import from OLTs
              </Button>
              <Button
                icon={<DatabaseBackup className="size-4" />}
                onClick={() => setSeeding(true)}
                title="Pick one stored config backup and add every model it declares — nothing already here is touched"
              >
                Seed from backup
              </Button>
              <Button
                variant="primary"
                icon={<Plus className="size-4" />}
                onClick={() => setCreating(true)}
              >
                New type
              </Button>
            </div>
          ) : null
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Models" value={hardware.length} icon={<Cpu className="size-4" />} />
        <StatCard
          label="ONUs covered"
          value={hardware.reduce((sum, h) => sum + h.onuCount, 0)}
          tone="success"
        />
        <StatCard
          label="Models with no port data"
          value={hardware.filter((h) => h.ethPorts === null).length}
          tone={hardware.some((h) => h.ethPorts === null) ? 'warn' : 'default'}
        />
        <StatCard label="TR-069 parameter maps" value={types.length} />
      </div>

      <Card
        className="mt-5"
        title={
          <span className="flex items-center gap-2">
            <Cpu className="size-4 text-muted" />
            Hardware
            <span className="text-xs font-normal text-faint">{hardware.length}</span>
          </span>
        }
      >
        <p className="border-b border-line px-5 py-2 text-xs text-faint">
          Port counts are read from what each OLT declares, never guessed — a model no config
          describes keeps blank counts rather than plausible ones. Edit a row and it stops being
          overwritten by later imports.
        </p>
        {hardwareQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : hardwareQuery.error ? (
          <div className="p-5">
            <ErrorNotice error={hardwareQuery.error} />
          </div>
        ) : hardware.length === 0 ? (
          <EmptyState
            icon={<Cpu className="size-8" />}
            title="No models yet"
            description="Import from the OLTs to read the models they declare, or add one by hand."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">Model</th>
              <th className="th">Vendor</th>
              <th className="th">PON</th>
              <th className="th">ONUs</th>
              <th className="th">Ethernet</th>
              <th className="th">WiFi SSIDs</th>
              <th className="th">Radios</th>
              <th className="th">VoIP</th>
              <th className="th">CATV</th>
              <th className="th">Source</th>
            </THead>
            <TBody>
              {[...hardware]
                .sort((a, b) => b.onuCount - a.onuCount || a.name.localeCompare(b.name))
                .map((h) => (
                  <TRow key={h.id} onClick={editable ? () => setEditingModel(h) : undefined}>
                    <td className="td font-medium">
                      <span className="flex items-center gap-2">
                        {hardwareImageUrl(h) && (
                          <img
                            src={hardwareImageUrl(h) ?? undefined}
                            alt=""
                            className="size-8 shrink-0 rounded border border-line bg-surface-2 object-contain"
                          />
                        )}
                        {h.name}
                      </span>
                    </td>
                    <td className="td text-muted">{h.vendor ?? '—'}</td>
                    <td className="td uppercase text-muted">{h.ponType}</td>
                    <td className="td tabular-nums">
                      {h.onuCount > 0 ? h.onuCount : <span className="text-faint">—</span>}
                    </td>
                    <Count value={h.ethPorts} />
                    <Count value={h.wifiSsids} />
                    <td className="td" title={h.ssidBandsSource ?? undefined}>
                      {h.ssidBands && h.ssidBands.length > 0 ? (
                        h.ssidBands.join(' + ')
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </td>
                    <Count value={h.voipPorts} />
                    <td className="td">
                      {h.catv === null ? (
                        <span className="text-faint">—</span>
                      ) : (
                        <Pill tone={h.catv ? 'primary' : 'neutral'}>{h.catv ? 'yes' : 'no'}</Pill>
                      )}
                    </td>
                    <td className="td text-xs" title={h.derivedFrom ?? undefined}>
                      {h.source === 'operator' ? (
                        <Pill tone="success">you set this</Pill>
                      ) : h.derivedFrom?.includes('published') ? (
                        <Pill tone="info">datasheet</Pill>
                      ) : (
                        <Pill tone="neutral">from the OLT</Pill>
                      )}
                    </td>
                  </TRow>
                ))}
            </TBody>
          </Table>
        )}
        {editable && hardware.length > 0 && (
          <p className="border-t border-line px-5 py-2 text-xs text-faint">
            Click a row to correct it. An edited row is yours from then on — no import overwrites
            it.
          </p>
        )}
        {missing.length > 0 && (
          <p className="border-t border-line px-5 py-2 text-xs text-warn">
            Running with no row here: {missing.map((m) => `${m.name} (${m.onuCount})`).join(', ')}.
            Import from the OLTs to add them.
          </p>
        )}
      </Card>

      {notice && (
        <div className="mt-4 rounded-lg border border-info/30 bg-info-soft px-4 py-2.5 text-sm text-info">
          {notice}
        </div>
      )}

      {unmapped.length > 0 && (
        <Card className="mt-5" title="Seen informing, no type yet">
          <Table>
            <THead>
              <th className="th">Manufacturer</th>
              <th className="th">Product class</th>
              <th className="th">Devices</th>
              <th className="th">Firmware seen</th>
              <th className="th" />
            </THead>
            <TBody>
              {unmapped.map((model) => (
                <TRow key={`${model.manufacturer}-${model.productClass}`}>
                  <td className="td">{model.manufacturer || '—'}</td>
                  <td className="td font-medium">{model.productClass}</td>
                  <td className="td tabular-nums">{model.deviceCount}</td>
                  <td className="td text-xs text-muted">
                    {model.softwareVersions.join(', ') || '—'}
                  </td>
                  <td className="td text-right">
                    {editable && (
                      <Button
                        size="sm"
                        variant="primary"
                        icon={<Sparkles className="size-3.5" />}
                        loading={discover.isPending && discover.variables === model.sampleDeviceId}
                        onClick={() => discover.mutate(model.sampleDeviceId)}
                      >
                        Discover from a device
                      </Button>
                    )}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      {/* What the night research job read off each model (operator, 2026-10-21:
          "research the different models and brands we add to the platform …
          learn through discovery of what the onu allows us to read from it").
          Reads only; the words seen are the vocabulary the gates need. */}
      <Card
        className="mt-5"
        title={
          <span className="flex items-center gap-2">
            Night research
            <span className="text-xs font-normal text-faint">{researchQuery.data?.items.length ?? 0}</span>
          </span>
        }
        action={
          editable ? (
            <Button size="sm" icon={<Sparkles className="size-3.5" />} loading={runResearch.isPending} onClick={() => runResearch.mutate()}>
              Research new models now
            </Button>
          ) : undefined
        }
      >
        <p className="px-5 pb-3 text-xs text-faint">
          Every night, one specimen of each newly seen model is read off the ACS: its tree, the dialects the platform
          recognises, the subscriber WAN's leaves, and every word any unit of the model has reported on its vendor
          leaves. Nothing is written to a unit. The "to verify" sentences are what to set once on the unit's own page
          so the next inform teaches the rest.
        </p>
        {researchNote && <p className="px-5 pb-3 text-xs text-muted">{researchNote}</p>}
        {(researchQuery.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Nothing researched yet" description="The job runs inside the nightly window; run it now to read the models the platform has seen." />
        ) : (
          <Table>
            <THead>
              <th className="th">Model</th>
              <th className="th">Units</th>
              <th className="th">Read</th>
              <th className="th">Dialects</th>
              <th className="th">Words</th>
              <th className="th">To verify</th>
            </THead>
            <TBody>
              {(researchQuery.data?.items ?? []).map((r) => (
                <TRow key={r.id}>
                  <td className="td">
                    <div className="font-medium">{r.manufacturer || '—'} {r.productClass}</div>
                    <div className="text-xs text-muted">
                      {r.brand ? `${r.brand} · ` : ''}
                      {r.softwareVersions.join(', ') || 'firmware unread'} · managed through {r.profileScope === 'none' ? 'discovered paths' : `the ${r.profileScope} profile`}
                    </div>
                  </td>
                  <td className="td tabular-nums">{r.deviceCount}</td>
                  <td className="td text-xs text-muted">
                    {formatTimestamp(r.researchedAt)}
                    <div>{r.parameters} parameters · {r.vendorSubtrees.length} vendor subtrees</div>
                  </td>
                  <td className="td text-xs">
                    <div>IPv6 {r.dialects.ipv6} · binding {r.dialects.ssidBinding ?? 'none'}</div>
                    <div className="text-muted">
                      VLAN {r.dialects.vlanKey ?? 'none'}{r.dialects.vlanOnParent ? ' (on the connection device)' : ''} · passphrase {r.dialects.passphraseLeaf ?? 'none'}
                    </div>
                  </td>
                  <td className="td text-xs">
                    <details>
                      <summary className="cursor-pointer select-none">{Object.keys(r.words).length} leaves</summary>
                      <ul className="mt-1 max-h-64 space-y-0.5 overflow-auto font-mono text-[11px]">
                        {Object.entries(r.words).map(([leaf, values]) => (
                          <li key={leaf}>
                            <span className="text-muted">{leaf.split('.').pop()}</span>: {values.join(' · ')}
                          </li>
                        ))}
                      </ul>
                    </details>
                  </td>
                  <td className="td text-xs text-muted">
                    {r.toVerify.length === 0 ? (
                      <Pill tone="success">nothing unread</Pill>
                    ) : (
                      <details>
                        <summary className="cursor-pointer select-none">{r.toVerify.length} to verify</summary>
                        <ul className="mt-1 list-disc space-y-1 pl-4">
                          {r.toVerify.map((s) => (
                            <li key={s}>{s}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Card
        className="mt-5"
        title={
          <span className="flex items-center gap-2">
            TR-069 parameter maps
            <span className="text-xs font-normal text-faint">{types.length}</span>
          </span>
        }
      >
        <p className="border-b border-line px-5 py-2 text-xs text-faint">
          Where each setting lives per CPE model, for ONUs managed over TR-069. Only a model that
          has informed this ACS can have one, so this stays empty on an OMCI-only fleet.
        </p>
        {typesQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : typesQuery.error ? (
          <div className="p-5">
            <ErrorNotice error={typesQuery.error} />
          </div>
        ) : types.length === 0 ? (
          <EmptyState
            icon={<Cpu className="size-8" />}
            title="No parameter maps yet"
            description="These appear once an ONU informs this ACS. Discover one from a device that is already informing, or create it by hand."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">Model</th>
              <th className="th">Data model</th>
              <th className="th">Mapped fields</th>
              <th className="th">WLAN instances</th>
              <th className="th">Devices</th>
              <th className="th">Source</th>
              <th className="th" />
            </THead>
            <TBody>
              {types.map((type) => (
                <TRow key={type.id} onClick={() => setEditing(type)}>
                  <td className="td">
                    <span className="font-medium">{type.manufacturer}</span>{' '}
                    <span className="text-muted">{type.productClass}</span>
                    {type.softwareVersion && (
                      <span className="ml-2 font-mono text-xs text-faint">
                        {type.softwareVersion}
                      </span>
                    )}
                  </td>
                  <td className="td uppercase text-muted">{type.dataModel}</td>
                  <td className="td tabular-nums">
                    {Object.keys(type.paths).length}
                    <span className="text-faint"> / {PARAM_FIELDS.length}</span>
                  </td>
                  <td className="td tabular-nums">
                    {type.wlanMap ? Object.keys(type.wlanMap).length : '—'}
                  </td>
                  <td className="td tabular-nums">{type.deviceCount ?? 0}</td>
                  <td className="td">
                    <Pill tone={type.curated ? 'primary' : 'neutral'}>
                      {type.curated ? 'curated' : 'discovered'}
                    </Pill>
                  </td>
                  <td className="td" onClick={(e) => e.stopPropagation()}>
                    {editable && (
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          icon={<Copy className="size-3.5" />}
                          onClick={() => setCloning(type)}
                        >
                          Clone
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<Trash2 className="size-3.5" />}
                          onClick={() => {
                            if (confirm(`Delete the ${type.productClass} type?`)) {
                              remove.mutate(type.id);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {/* Every dialog below closes on submit; the write and its outcome live in
          the tasks tray, which refreshes the list keys itself. */}
      {editingModel && (
        <EditHardwareDialog model={editingModel} onClose={() => setEditingModel(null)} />
      )}
      {editing && <EditTypeDialog type={editing} onClose={() => setEditing(null)} />}
      {cloning && <CloneTypeDialog source={cloning} onClose={() => setCloning(null)} />}
      {creating && <CreateTypeDialog onClose={() => setCreating(false)} />}
      {seeding && <SeedFromBackupDialog onClose={() => setSeeding(false)} />}
    </>
  );
}

/** The body classes every dialog on this page shares: a tall scrolling form. */
const TYPE_DIALOG_BODY = 'max-h-[70vh] space-y-5 overflow-y-auto p-5';

/** Editor for the path map and WLAN instance labels. */
function PathEditor({
  paths,
  onChange,
  inheritedFrom,
}: {
  paths: Partial<Record<ParamField, string>>;
  onChange: (next: Partial<Record<ParamField, string>>) => void;
  inheritedFrom?: Partial<Record<ParamField, string>>;
}) {
  return (
    <div className="space-y-2">
      {PARAM_FIELDS.map((field) => {
        const inherited = inheritedFrom?.[field];
        const value = paths[field] ?? '';
        const isOverride = Boolean(inherited && value && value !== inherited);

        return (
          <div key={field} className="grid grid-cols-[10rem_1fr] items-center gap-3">
            <label className="text-sm text-muted">
              {PARAM_FIELD_LABELS[field]}
              {isOverride && (
                <span className="ml-1.5 text-[10px] uppercase tracking-wide text-primary">
                  override
                </span>
              )}
            </label>
            <Input
              value={value}
              onChange={(e) => {
                const next = { ...paths };
                if (e.target.value.trim()) next[field] = e.target.value;
                else delete next[field];
                onChange(next);
              }}
              placeholder={inherited ?? 'not mapped'}
              className="font-mono text-xs"
            />
          </div>
        );
      })}
    </div>
  );
}

function WlanEditor({
  wlanMap,
  onChange,
}: {
  wlanMap: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  const entries = Object.entries(wlanMap);

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted">
        Which WLAN instance is which radio. The ZTE F670L, for example, puts the 2.4 GHz SSIDs on
        instances 1 and 2 and the 5 GHz ones on 5 and 6 — nothing in the data model says so.
      </p>
      {entries.map(([instance, label]) => (
        <div key={instance} className="grid grid-cols-[6rem_1fr_auto] gap-2">
          <Input
            value={instance}
            onChange={(e) => {
              const next: Record<string, string> = {};
              for (const [key, val] of entries) {
                next[key === instance ? e.target.value : key] = val;
              }
              onChange(next);
            }}
            className="font-mono"
            placeholder="1"
          />
          <Input
            value={label}
            onChange={(e) => onChange({ ...wlanMap, [instance]: e.target.value })}
            placeholder="2.4G SSID1"
          />
          <Button
            variant="ghost"
            icon={<X className="size-4" />}
            onClick={() => {
              const next = { ...wlanMap };
              delete next[instance];
              onChange(next);
            }}
          />
        </div>
      ))}
      <Button
        size="sm"
        icon={<Plus className="size-3.5" />}
        onClick={() => onChange({ ...wlanMap, [String(entries.length + 1)]: '' })}
      >
        Add instance
      </Button>
    </div>
  );
}

function EditTypeDialog({ type, onClose }: { type: OnuType; onClose: () => void }) {
  const tasks = useTasks();
  const [label, setLabel] = useState(type.label ?? '');
  const [dataModel, setDataModel] = useState<DataModel>(type.dataModel);
  const [paths, setPaths] = useState(type.paths);
  const [wlanMap, setWlanMap] = useState<Record<string, string>>(type.wlanMap ?? {});

  const name = `${type.manufacturer} ${type.productClass}`;
  // Save closes; the write and its outcome live in the tray.
  const save = () => {
    const body = {
      label,
      dataModel,
      paths,
      wlanMap: Object.keys(wlanMap).length > 0 ? wlanMap : null,
    };
    onClose();
    tasks.start({
      label: `Save type ${name}`,
      run: () => api.patch<OnuType>(`/onu-types/${encodeURIComponent(type.id)}`, body),
      done: () => `${name} saved and marked curated.`,
      invalidate: [['onu-types']],
    });
  };

  return (
    <WizardDialog
      title={name}
      subtitle={`${type.deviceCount ?? 0} device(s) use this type. Saving marks it curated, so discovery will not overwrite it.`}
      onClose={onClose}
      maxWidth="3xl"
      bodyClassName={TYPE_DIALOG_BODY}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save}>
            Save
          </Button>
        </div>
      }
    >
      <>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Label">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} />
          </Field>
          <Field label="Data model">
            <select
              className="input"
              value={dataModel}
              onChange={(e) => setDataModel(e.target.value as DataModel)}
            >
              <option value="tr098">TR-098 (InternetGatewayDevice)</option>
              <option value="tr181">TR-181 (Device)</option>
              <option value="unknown">Unknown</option>
            </select>
          </Field>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium text-ink">Parameter paths</h3>
          <PathEditor paths={paths} onChange={setPaths} />
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium text-ink">WLAN instances</h3>
          <WlanEditor wlanMap={wlanMap} onChange={setWlanMap} />
        </div>
      </>
    </WizardDialog>
  );
}

function CloneTypeDialog({ source, onClose }: { source: OnuType; onClose: () => void }) {
  const tasks = useTasks();
  const [manufacturer, setManufacturer] = useState(source.manufacturer);
  const [productClass, setProductClass] = useState('');
  const [overrides, setOverrides] = useState<Partial<Record<ParamField, string>>>({});

  // Submit closes; the write and its outcome live in the tray.
  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const body = {
      manufacturer: manufacturer.trim(),
      productClass: productClass.trim(),
      softwareVersion: '',
      overrides,
    };
    const name = `${body.manufacturer} ${body.productClass}`;
    onClose();
    tasks.start({
      label: `Clone ${source.manufacturer} ${source.productClass} → ${name}`,
      run: () => api.post<OnuType>(`/onu-types/${encodeURIComponent(source.id)}/clone`, body),
      done: () => `${name} created from ${source.manufacturer} ${source.productClass}.`,
      invalidate: [['onu-types']],
    });
  }

  return (
    <WizardDialog
      title={`Clone ${source.manufacturer} ${source.productClass}`}
      subtitle="Everything is inherited. Fill in only the paths that differ on the new model."
      onClose={onClose}
      maxWidth="3xl"
      bodyClassName={TYPE_DIALOG_BODY}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="clone-type-form" variant="primary">
            Create
          </Button>
        </div>
      }
    >
      <form id="clone-type-form" className="space-y-5" onSubmit={onSubmit}>
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Manufacturer">
              <Input
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                required
              />
            </Field>
            <Field label="Product class" hint="Exactly as the CPE reports it">
              <Input
                value={productClass}
                onChange={(e) => setProductClass(e.target.value)}
                placeholder="F660"
                required
                autoFocus
              />
            </Field>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium text-ink">Overrides</h3>
            <PathEditor
              paths={overrides}
              onChange={setOverrides}
              inheritedFrom={source.paths}
            />
          </div>
        </>
      </form>
    </WizardDialog>
  );
}

function CreateTypeDialog({ onClose }: { onClose: () => void }) {
  const tasks = useTasks();
  const [manufacturer, setManufacturer] = useState('');
  const [productClass, setProductClass] = useState('');
  const [dataModel, setDataModel] = useState<DataModel>('tr098');
  const [paths, setPaths] = useState<Partial<Record<ParamField, string>>>({});

  // Submit closes; the write and its outcome live in the tray.
  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const body = {
      manufacturer: manufacturer.trim(),
      productClass: productClass.trim(),
      softwareVersion: '',
      dataModel,
      paths,
      curated: true,
    };
    const name = `${body.manufacturer} ${body.productClass}`;
    onClose();
    tasks.start({
      label: `New ONU type ${name}`,
      run: () => api.post<OnuType>('/onu-types', body),
      done: () => `${name} created.`,
      invalidate: [['onu-types']],
    });
  }

  return (
    <WizardDialog
      title="New ONU type"
      subtitle="Usually easier to discover one from a device that is already informing, then edit it."
      onClose={onClose}
      maxWidth="3xl"
      bodyClassName={TYPE_DIALOG_BODY}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="create-type-form" variant="primary">
            Create
          </Button>
        </div>
      }
    >
      <form id="create-type-form" className="space-y-5" onSubmit={onSubmit}>
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Manufacturer">
              <Input
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                placeholder="ZTE"
                required
                autoFocus
              />
            </Field>
            <Field label="Product class">
              <Input
                value={productClass}
                onChange={(e) => setProductClass(e.target.value)}
                placeholder="F670L"
                required
              />
            </Field>
            <Field label="Data model">
              <select
                className="input"
                value={dataModel}
                onChange={(e) => setDataModel(e.target.value as DataModel)}
              >
                <option value="tr098">TR-098</option>
                <option value="tr181">TR-181</option>
                <option value="unknown">Unknown</option>
              </select>
            </Field>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium text-ink">Parameter paths</h3>
            <PathEditor paths={paths} onChange={setPaths} />
          </div>
        </>
      </form>
    </WizardDialog>
  );
}

/** What a seeded model would bring, in the operator's own shorthand. */
function seededCaps(m: SeededOnuModel): string {
  const parts: string[] = [];
  if (m.ethPorts !== null) parts.push(`${m.ethPorts} eth`);
  if (m.wifiSsids !== null) parts.push(`${m.wifiSsids} wifi`);
  if (m.voipPorts !== null) parts.push(`${m.voipPorts} voip`);
  if (m.catv !== null) parts.push(m.catv ? 'catv' : 'no catv');
  return parts.length > 0 ? parts.join(' · ') : 'no counts stated';
}

/**
 * Seed the library from ONE stored config backup.
 *
 * The fresh-install path: pick a chassis, pick one of its backups, and every
 * model that config declares becomes a row — with the preview shown FIRST,
 * because the contract is additive and the operator should see created-vs-held
 * before anything is written. A model already in the library is held untouched
 * whatever its source; that is what makes this a seed rather than an import.
 *
 * Two steps: pick the backup, then review the dry run. The dry run is a read
 * and stays in the dialog; the seed itself CLOSES the dialog and runs in the
 * tasks tray, whose toast carries the created-vs-held count.
 */
const SEED_STEPS = [
  { key: 'backup', title: 'Backup' },
  { key: 'review', title: 'Review' },
];

function SeedFromBackupDialog({ onClose }: { onClose: () => void }) {
  const tasks = useTasks();
  const [step, setStep] = useState(0);
  const [oltId, setOltId] = useState('');
  const [backupId, setBackupId] = useState('');
  const [preview, setPreview] = useState<SeedOnuTypesResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const oltsQuery = useQuery({
    queryKey: ['olts'],
    queryFn: () => api.get<Page<Olt>>('/olts'),
  });
  const backupsQuery = useQuery({
    queryKey: ['olt', oltId, 'backups'],
    queryFn: () => api.get<Page<OltBackup>>(`/olts/${encodeURIComponent(oltId)}/backups`),
    enabled: Boolean(oltId),
  });
  const oltRows = oltsQuery.data?.items ?? [];
  const backups = backupsQuery.data?.items ?? [];

  const seedPath = `/olts/${encodeURIComponent(oltId)}/onu-types/seed`;
  // The dry run: a read, so it stays in the dialog — the operator reads
  // created-vs-held on the Review step before the seed ever fires.
  const dryRun = useMutation({
    mutationFn: () => api.post<SeedOnuTypesResult>(seedPath, { backupId, dryRun: true }),
    onSuccess: (result) => {
      setError(null);
      setPreview(result);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Preview failed'),
  });

  const created = preview?.models.filter((m) => m.action === 'created') ?? [];
  const held = preview?.models.filter((m) => m.action === 'held') ?? [];

  const goReview = () => {
    setPreview(null);
    setStep(1);
    dryRun.mutate();
  };

  // Seed closes; the write and its count live in the tray.
  const startSeed = () => {
    const body = { backupId, dryRun: false };
    const path = seedPath;
    const count = created.length;
    onClose();
    tasks.start<SeedOnuTypesResult>({
      label: `Seed ${count} model${count === 1 ? '' : 's'} from a backup`,
      run: () => api.post<SeedOnuTypesResult>(path, body),
      done: (r) =>
        `Seeded ${r.created} model${r.created === 1 ? '' : 's'} from the backup` +
        (r.held > 0 ? `; ${r.held} already in the library and left untouched.` : '.'),
      invalidate: [['onu-hardware']],
    });
  };

  return (
    <WizardDialog
      title="Seed from a config backup"
      subtitle="Adds every model the backup declares, with the capabilities its config proves. Nothing already in the library is touched."
      onClose={onClose}
      maxWidth="3xl"
      bodyClassName={TYPE_DIALOG_BODY}
      steps={SEED_STEPS}
      currentStep={step}
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(0)}>
            Back
          </Button>
          <div className="flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            {step === 0 ? (
              <Button variant="primary" disabled={!backupId} onClick={goReview}>
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                disabled={!preview || created.length === 0}
                onClick={startSeed}
              >
                Apply
              </Button>
            )}
          </div>
        </div>
      }
    >
      <>
        {step === 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="OLT">
              <select
                className="input"
                value={oltId}
                onChange={(e) => {
                  setOltId(e.target.value);
                  setBackupId('');
                  setPreview(null);
                }}
              >
                <option value="">Choose an OLT…</option>
                {oltRows.map((olt) => (
                  <option key={olt.id} value={String(olt.number ?? olt.id)}>
                    {olt.number != null ? `#${olt.number} — ${olt.name}` : olt.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Backup"
              hint={
                oltId && backups.length === 0 && !backupsQuery.isLoading
                  ? 'This OLT has no stored backups yet.'
                  : undefined
              }
            >
              <select
                className="input"
                value={backupId}
                disabled={!oltId}
                onChange={(e) => {
                  setBackupId(e.target.value);
                  setPreview(null);
                }}
              >
                <option value="">{oltId ? 'Choose a backup…' : 'Choose an OLT first'}</option>
                {backups.map((backup) => (
                  <option key={backup.id} value={backup.id}>
                    {formatTimestamp(backup.takenAt)} — {backup.kind},{' '}
                    {Math.round(backup.sizeBytes / 1024)} KB
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}

        {step === 1 && dryRun.isPending && (
          <div className="flex items-center justify-center gap-3 p-6">
            <Spinner />
            <span className="text-sm text-muted">Reading the backup…</span>
          </div>
        )}

        {step === 1 && preview && (
          <div className="space-y-3">
            {preview.warnings.map((warning) => (
              <p
                key={warning}
                className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn"
              >
                {warning}
              </p>
            ))}
            {created.length > 0 && (
              <div>
                <h3 className="mb-1 text-sm font-medium text-ink">
                  Would be created ({created.length})
                </h3>
                <ul className="divide-y divide-line rounded-lg border border-line">
                  {created.map((m) => (
                    <li key={m.name} className="flex items-baseline justify-between gap-3 px-3 py-1.5 text-sm">
                      <span className="font-medium">{m.name}</span>
                      <span className="text-xs text-muted">{seededCaps(m)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {held.length > 0 && (
              <div>
                <h3 className="mb-1 text-sm font-medium text-ink">
                  Already in the library — held untouched ({held.length})
                </h3>
                <ul className="divide-y divide-line rounded-lg border border-line">
                  {held.map((m) => (
                    <li key={m.name} className="px-3 py-1.5 text-sm text-muted">
                      {m.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {created.length === 0 && preview.warnings.length === 0 && (
              <p className="text-sm text-muted">
                Every model this backup declares is already in the library. There is nothing to
                seed.
              </p>
            )}
          </div>
        )}

        {error && <ErrorNotice error={new Error(error)} />}
      </>
    </WizardDialog>
  );
}

/** A count the OLT stated, or a dash where nothing did. Blank is not zero. */
function Count({ value }: { value: number | null }) {
  return (
    <td className="td tabular-nums">
      {value === null ? <span className="text-faint" title="Not stated by any config">—</span> : value}
    </td>
  );
}

/**
 * Correct one model by hand.
 *
 * Saving marks the row as the operator's, and no import touches it again. That
 * is the point: somebody with the hardware on the bench outranks both a config
 * parse and a datasheet, and a library that quietly reverted their correction
 * would not be worth keeping. Blank means "not known" and stays null rather
 * than being written as a zero nobody measured.
 */
function EditHardwareDialog({ model, onClose }: { model: OnuHardware; onClose: () => void }) {
  const tasks = useTasks();
  const [form, setForm] = useState({
    name: model.name,
    vendor: model.vendor ?? '',
    ponType: model.ponType,
    ethPorts: model.ethPorts === null ? '' : String(model.ethPorts),
    wifiSsids: model.wifiSsids === null ? '' : String(model.wifiSsids),
    ssidBands: model.ssidBands?.includes('5G') ? 'dual' : model.ssidBands?.length ? '24' : '',
    voipPorts: model.voipPorts === null ? '' : String(model.voipPorts),
    catv: model.catv === null ? '' : String(model.catv),
    notes: model.notes ?? '',
  });
  /**
   * The picture is STAGED here and sent by Save, so the dialog keeps the
   * one rule every modal follows: submit closes, the work runs in the tray.
   * A file over the limit is refused at the picker — the API checks the
   * bytes and is the one that counts, but a 4 MB round trip that was always
   * going to fail is a slow way to learn that.
   */
  const [photo, setPhoto] = useState<File | null>(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const currentUrl = hardwareImageUrl(model);
  const previewUrl = photo ? URL.createObjectURL(photo) : removePhoto ? null : currentUrl;

  const num = (value: string): number | null => (value.trim() === '' ? null : Number(value));

  // Save closes; the write and its outcome live in the tray.
  const save = () => {
    const body = {
      name: form.name.trim(),
      vendor: form.vendor || null,
      ponType: form.ponType,
      ethPorts: num(form.ethPorts),
      wifiSsids: num(form.wifiSsids),
      // Blank clears the record back to unrecorded, which puts this model
      // back on the fleet-wide layout rather than asserting anything.
      ssidBands:
        form.ssidBands === '' ? null : form.ssidBands === 'dual' ? ['2.4G', '5G'] : ['2.4G'],
      voipPorts: num(form.voipPorts),
      catv: form.catv === '' ? null : form.catv === '1',
      notes: form.notes.trim() || null,
    };
    const name = body.name || model.name;
    const staged = photo;
    const clearing = removePhoto && !photo;
    onClose();
    tasks.start({
      label: `Save ${name}`,
      run: async () => {
        await api.patch(`/onu-hardware/${encodeURIComponent(model.id)}`, body);
        if (staged) await uploadHardwareImage(model.id, staged);
        else if (clearing) await removeHardwareImage(model.id);
      },
      done: () =>
        `${name} saved — this row is yours now; no import touches it again.` +
        (staged ? ' Picture uploaded.' : clearing ? ' Picture removed.' : ''),
      invalidate: [['onu-hardware']],
    });
  };

  return (
    <WizardDialog
      title={model.name}
      subtitle={
        (model.onuCount > 0
          ? `${model.onuCount} ONUs on your network run this. `
          : 'No ONU on your network reports this model yet. ') +
        'A blank field means not known, and is kept that way rather than saved as zero.'
      }
      onClose={onClose}
      maxWidth="xl"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save}>
            Save
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Model">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Vendor">
          <select
            className="input"
            value={form.vendor}
            onChange={(e) => setForm({ ...form, vendor: e.target.value })}
          >
            <option value="">Not set</option>
            <option value="huawei">Huawei</option>
            <option value="zte">ZTE</option>
          </select>
        </Field>
        <Field label="PON type">
          <select
            className="input"
            value={form.ponType}
            onChange={(e) => setForm({ ...form, ponType: e.target.value })}
          >
            <option value="gpon">GPON</option>
            <option value="epon">EPON</option>
          </select>
        </Field>
        <Field label="CATV">
          <select
            className="input"
            value={form.catv}
            onChange={(e) => setForm({ ...form, catv: e.target.value })}
          >
            <option value="">Not known</option>
            <option value="1">Has an RF port</option>
            <option value="0">No RF port</option>
          </select>
        </Field>
        <Field label="Ethernet ports">
          <Input
            value={form.ethPorts}
            onChange={(e) => setForm({ ...form, ethPorts: e.target.value })}
            placeholder="blank = not known"
          />
        </Field>
        <Field label="WiFi SSIDs">
          <Input
            value={form.wifiSsids}
            onChange={(e) => setForm({ ...form, wifiSsids: e.target.value })}
            placeholder="blank = not known"
          />
        </Field>
        <Field
          label="Radios"
          hint={
            model.ssidBandsSource
              ? `Recorded from: ${model.ssidBandsSource}`
              : 'Blank means unrecorded — the fleet SSID layout applies'
          }
        >
          <select
            className="input"
            value={form.ssidBands}
            onChange={(e) => setForm({ ...form, ssidBands: e.target.value })}
          >
            <option value="">Not known</option>
            <option value="24">2.4 GHz only</option>
            <option value="dual">2.4 GHz + 5 GHz</option>
          </select>
        </Field>
        <Field label="VoIP ports">
          <Input
            value={form.voipPorts}
            onChange={(e) => setForm({ ...form, voipPorts: e.target.value })}
            placeholder="blank = not known"
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Notes" hint={model.derivedFrom ?? undefined}>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything worth knowing about this model"
            />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field
            label="Picture"
            hint="PNG, JPEG, WebP or GIF, up to 4 MB. Shown beside the model here, and served to SmartOLT-compatible clients as the ONU type image."
          >
            <div className="flex items-center gap-3">
              <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-line bg-surface-2">
                {previewUrl ? (
                  <img src={previewUrl} alt="" className="size-full object-contain" />
                ) : (
                  <span className="text-[10px] uppercase tracking-wide text-faint">none</span>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept={OLT_IMAGE_CONTENT_TYPES.join(',')}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  e.target.value = '';
                  if (!file) return;
                  if (file.size > OLT_IMAGE_MAX_BYTES) {
                    setPhotoError(
                      `That image is ${Math.round((file.size / 1024 / 1024) * 10) / 10} MB and the limit is ${OLT_IMAGE_MAX_BYTES / 1024 / 1024} MB.`,
                    );
                    return;
                  }
                  setPhotoError(null);
                  setRemovePhoto(false);
                  setPhoto(file);
                }}
              />
              <Button
                size="sm"
                variant="ghost"
                icon={<ImageUp className="size-3.5" />}
                onClick={() => fileRef.current?.click()}
              >
                {previewUrl ? 'Replace picture' : 'Add picture'}
              </Button>
              {(photo || (currentUrl && !removePhoto)) && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="size-3.5" />}
                  onClick={() => {
                    setPhoto(null);
                    setRemovePhoto(Boolean(currentUrl));
                  }}
                >
                  {photo ? 'Discard' : 'Remove picture'}
                </Button>
              )}
              {photoError && <span className="text-xs text-danger">{photoError}</span>}
            </div>
          </Field>
        </div>
      </div>
    </WizardDialog>
  );
}
